import React, { useState } from 'react';
import { QAWidgetInstanceConfig, QADataContent, QAItem, ConvictionDataContent, ImageRepositoryDataContent } from '../../../../../types';
import { WidgetEmptyState } from '../../../design';

// Union type for all dashboard data content types
type DashboardDataContent = QADataContent | ConvictionDataContent | ImageRepositoryDataContent;

interface QAWidgetRendererProps {
  config: QAWidgetInstanceConfig;  // Uses instance config which includes dataKey
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: DashboardDataContent) => void;
  readOnly?: boolean;
}

const QAWidgetRenderer: React.FC<QAWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  // Get Q&A data from dashboard data using the dataKey
  const qaData = dashboardData[config.dataKey] as QADataContent | undefined;

  // Accordion starts with the FIRST question expanded so the analyst sees a
  // writing surface immediately — collapsed-by-default hid every input behind a
  // click on the steps that use this layout.
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() =>
    config.layout === 'accordion' && qaData?.items?.length
      ? new Set([qaData.items[0].id])
      : new Set(),
  );

  const toggleAccordion = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleAnswerChange = (itemId: string, newAnswer: string) => {
    if (!qaData || !onDataChange) return;

    const updatedItems = qaData.items.map((item) =>
      item.id === itemId ? { ...item, answer: newAnswer } : item
    );

    onDataChange(config.dataKey, {
      ...qaData,
      items: updatedItems,
    });
  };

  // No data key configured
  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  // No data found for the key
  if (!qaData || !qaData.items || qaData.items.length === 0) {
    return (
      <WidgetEmptyState
        title="No questions configured"
        description="Add questions in the Workflow Builder step configuration."
      />
    );
  }

  // Inline validation message for a single item. Mirrors the registry
  // validator (qa/index.ts) so the worker sees the same rule that gates
  // step completion: required-empty, non-numeric number, invalid select.
  const getItemError = (item: QAItem): string | null => {
    const answer = item.answer?.trim() ?? '';
    if (!answer) return item.required ? 'Required field' : null;
    if (item.answerType === 'number' && !Number.isFinite(Number(answer))) {
      return 'Must be a number';
    }
    if (
      item.answerType === 'select' &&
      item.options?.length &&
      !item.options.includes(answer)
    ) {
      return 'Select a valid option';
    }
    return null;
  };

  const renderAnswerField = (item: QAItem, fill = false) => {
    const canEdit = !readOnly;
    const error = canEdit ? getItemError(item) : null;

    const baseInputClasses = "w-full px-4 py-3 rounded-lg rounded-br-sm text-sm transition-all bg-surface-secondary border";
    const normalClasses = `${baseInputClasses} border-neutral-600 text-neutral-800 placeholder-neutral-400 focus:border-neutral-400 focus:outline-none`;
    const warningClasses = `${baseInputClasses} border-danger-500 text-neutral-800 placeholder-neutral-400 focus:border-danger-400 focus:outline-none`;
    const inputClasses = error ? warningClasses : normalClasses;

    if (!canEdit) {
      return (
        <div className="px-4 py-3 bg-surface-secondary rounded-lg border border-neutral-600">
          <p className="text-sm text-neutral-600">
            {item.answer || <span className="italic text-neutral-500">No answer provided</span>}
          </p>
        </div>
      );
    }

    const onChange = (value: string) => handleAnswerChange(item.id, value);

    let field: React.ReactNode;
    if (item.answerType === 'select') {
      field = (
        <select
          value={item.answer || ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClasses}
        >
          <option value="">Select an option...</option>
          {(item.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    } else if (item.answerType === 'number') {
      field = (
        <input
          type="number"
          value={item.answer || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter a number..."
          className={inputClasses}
        />
      );
    } else if (item.answerType === 'text') {
      field = (
        <input
          type="text"
          value={item.answer || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your answer..."
          className={inputClasses}
        />
      );
    } else {
      field = (
        <textarea
          value={item.answer || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your answer..."
          // fill: grow to occupy the tile (adapts to any height); otherwise a
          // fixed 2-row box the user can drag taller.
          className={`${inputClasses} ${fill ? 'flex-1 min-h-0 resize-none' : 'resize-y'}`}
          {...(fill ? {} : { rows: 2 })}
        />
      );
    }

    return (
      <div className={fill ? 'flex-1 flex flex-col min-h-0' : undefined}>
        {field}
        {error && (
          <p className="text-xs text-danger-500 mt-1.5 font-medium">{error}</p>
        )}
      </div>
    );
  };

  const renderQuestion = (item: QAItem) => (
    <div className="mb-2">
      <p className="font-bold text-sm text-neutral-800">
        {item.question}
        {item.required && <span className="text-danger-500 ml-1">*</span>}
      </p>
      {item.description && (
        <p className="text-xs text-neutral-400 mt-0.5">{item.description}</p>
      )}
    </div>
  );

  // A lone free-text note (e.g. the Set-up "What is the setup into the print?"
  // box) should fill whatever height the tile has, so it adapts cleanly to both
  // compressed and roomy layouts instead of a fixed-height box that over/underflows.
  const isSingleNote =
    qaData.items.length === 1 &&
    !['select', 'number', 'text'].includes(qaData.items[0]?.answerType ?? '');

  const renderVerticalLayout = () => {
    if (isSingleNote) {
      const item = qaData.items[0];
      return (
        <div className="h-full flex flex-col min-h-0">
          {renderQuestion(item)}
          {renderAnswerField(item, true)}
        </div>
      );
    }
    return (
      <div className="space-y-5">
        {qaData.items.map((item) => (
          <div key={item.id}>
            {renderQuestion(item)}
            {renderAnswerField(item)}
          </div>
        ))}
      </div>
    );
  };

  const renderHorizontalLayout = () => (
    <div className="space-y-4">
      {qaData.items.map((item) => (
        <div key={item.id} className="flex gap-5 items-start">
          <div className="flex-1">
            {renderQuestion(item)}
          </div>
          <div className="flex-1">
            {renderAnswerField(item)}
          </div>
        </div>
      ))}
    </div>
  );

  const renderAccordionLayout = () => (
    // Tight spacing/padding so a collapsed checklist of several questions fits a
    // compressed tile (e.g. Set-up's h4) without scrolling.
    <div className="space-y-2">
      {qaData.items.map((item) => {
        const isExpanded = expandedItems.has(item.id);
        return (
          <div key={item.id} className="rounded-lg border border-neutral-600 overflow-hidden">
            <button
              onClick={() => toggleAccordion(item.id)}
              className="w-full px-4 py-2 flex items-center justify-between hover:bg-neutral-700/30 transition-colors"
            >
              <span className="font-bold text-sm leading-snug text-neutral-800 text-left">
                {item.question}
                {item.required && <span className="text-danger-500 ml-1">*</span>}
              </span>
              <svg
                className={`w-4 h-4 text-neutral-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isExpanded && (
              <div className="px-4 py-2 border-t border-neutral-600">
                {item.description && (
                  <p className="text-xs text-neutral-400 mb-2">{item.description}</p>
                )}
                {renderAnswerField(item)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const verticalFill = isSingleNote && (config.layout === 'vertical' || !config.layout);

  return (
    <div className={`p-3 bg-surface ${verticalFill ? 'h-full flex flex-col overflow-auto' : 'h-full overflow-auto'}`}>
      {/* Q&A Content */}
      {config.layout === 'horizontal' && renderHorizontalLayout()}
      {config.layout === 'accordion' && renderAccordionLayout()}
      {(config.layout === 'vertical' || !config.layout) && renderVerticalLayout()}
    </div>
  );
};

export default QAWidgetRenderer;
